// API 根地址由 backend/.env 经 vue.config.js 注入（VUE_APP_*），勿在本文件写死 URL

const apiBase = (process.env.VUE_APP_API_BASE_URL || '').replace(/\/$/, '')
const wsBase = (process.env.VUE_APP_WS_BASE_URL || '').replace(/\/$/, '')
const staticBase = (process.env.VUE_APP_STATIC_BASE_URL || apiBase).replace(
  /\/$/,
  '',
)

const nodeEnv = process.env.NODE_ENV || 'development'

export const API_CONFIG = {
  CURRENT_ENV: nodeEnv === 'production' ? 'production' : 'development',

  API_BASE_URL: apiBase,
  WS_URL: wsBase,
  STATIC_BASE_URL: staticBase,

  ENDPOINTS: {
    USER_LOGIN: '/api/users/login',
    USER_REGISTER: '/api/users/register',
    USER_PROFILE: '/api/users/profile',
    USER_UPLOAD_AVATAR: '/api/users/upload-avatar',

    VIDEOS: '/api/videos/',
    VIDEO_UPLOAD: '/api/videos/upload',
    VIDEO_CLASSIFY: '/api/videos/classify',

    VIDEO_CATEGORIES: '/api/video-categories/',

    CHAT: '/api/chat',
    CHAT_IMAGE: '/api/chat/image',
    CHAT_VIDEO: '/api/chat/video',

    CONVERSATIONS: '/api/conversations',
    CONVERSATION_DETAIL: '/api/conversations/',
  },

  STATIC_PATHS: {
    AVATARS: '/uploads/avatars',
    VIDEO_COVERS: '/uploads/covers',
    VIDEOS: '/uploads/videos',
  },
}

export function getStaticUrl(relativePath) {
  if (!relativePath) return ''

  if (
    relativePath.startsWith('http://') ||
    relativePath.startsWith('https://')
  ) {
    return relativePath
  }

  const p = relativePath.startsWith('/') ? relativePath : `/${relativePath}`

  return `${API_CONFIG.STATIC_BASE_URL}${p}`
}

/** 兼容历史相对路径（如 user_1/chat_media/x.jpg）→ 可访问的 /storage/... URL */
export function getStorageFileUrl(relativePath) {
  if (!relativePath) return ''
  const s = String(relativePath).trim()
  if (
    s.startsWith('http://') ||
    s.startsWith('https://') ||
    s.startsWith('blob:')
  ) {
    return s
  }
  const clean = s.replace(/^\/+/, '')
  if (clean.startsWith('storage/')) {
    return `${API_CONFIG.STATIC_BASE_URL}/${clean}`
  }
  return `${API_CONFIG.STATIC_BASE_URL}/storage/${clean}`
}

export function getApiUrl(endpoint) {
  if (endpoint.startsWith('http://') || endpoint.startsWith('https://')) {
    return endpoint
  }

  const p = endpoint.startsWith('/') ? endpoint : `/${endpoint}`
  return `${API_CONFIG.API_BASE_URL}${p}`
}

console.log(`🔧 API配置: env=${API_CONFIG.CURRENT_ENV} base=${API_CONFIG.API_BASE_URL}`)

export async function getImageWithAuth(url) {
  try {
    const axios = await import('axios')
    const token = localStorage.getItem('token')

    const headers = {
      'ngrok-skip-browser-warning': 'any',
    }

    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    const response = await axios.default.get(url, {
      responseType: 'blob',
      headers: headers,
    })
    return URL.createObjectURL(response.data)
  } catch (error) {
    console.error('获取资源失败:', error)
    return null
  }
}

/**
 * 去掉常见非法路径/文件名字符与控制字符（不显式写进正则，以满足 no-control-regex）。
 * @param {string} raw
 * @param {{ fallback?: string, maxLength?: number }} [opts]
 */
export function sanitizeDownloadBasename(raw, opts = {}) {
  const { fallback = 'video', maxLength } = opts
  let out = ''
  for (const ch of String(raw ?? '')) {
    const code = ch.charCodeAt(0)
    if (code < 32) {
      out += '_'
    } else if ('<>:"/\\|?*'.includes(ch)) {
      out += '_'
    } else {
      out += ch
    }
  }
  out = out.trim()
  const limit = typeof maxLength === 'number' ? maxLength : null
  if (limit != null && limit > 0 && out.length > limit) {
    out = out.slice(0, limit)
  }
  return out || fallback
}

/**
 * 将资源以 Blob 拉取后触发浏览器「保存」对话框，避免跨域直链仅靠 a.download 时被当作在线播放。
 * @param {string} url
 * @param {string} filename 建议文件名（含扩展名）
 * @param {{ useAuth?: boolean }} [opts] 默认附带 Bearer（与静态资源鉴权一致）
 */
export async function downloadUrlAsFile(url, filename, opts = {}) {
  const { useAuth = true } = opts
  if (!url) {
    throw new Error('缺少 URL')
  }

  const axiosMod = await import('axios')
  const headers = {
    'ngrok-skip-browser-warning': 'any',
  }
  if (useAuth) {
    const token = localStorage.getItem('token')
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
  }

  const response = await axiosMod.default.get(url, {
    responseType: 'blob',
    headers,
  })

  const blobUrl = URL.createObjectURL(response.data)
  const link = document.createElement('a')
  link.href = blobUrl
  link.download = filename || 'download'
  link.rel = 'noopener noreferrer'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000)
}

export default API_CONFIG
